import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Save, Eye, X, BellRing, BellOff } from "lucide-react";

const inputCls = "w-full bg-obsidian border border-white/10 rounded-md text-white px-3 py-2.5 text-sm focus:border-gold focus:ring-1 focus:ring-gold outline-none";

function PreviewModal({ group, days, onClose }) {
  const [type, setType] = useState(days.length ? "upcoming" : "expired");
  const [selectedDays, setSelectedDays] = useState(days[0] || 7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchPreview = async () => {
    setBusy(true);
    try {
      const res = await api.previewMembershipNotice(group.chatId, type, type === "upcoming" ? selectedDays : undefined);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar la previsualización");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="membership-preview-modal">
        <div className="flex items-center justify-between mb-1">
          <span className="overline text-gold">Vista previa · {group.name || group.chatId}</span>
          <button data-testid="membership-preview-close" onClick={onClose} className="text-muted-foreground hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Esto es solo una vista previa. No se envía ningún mensaje real.</p>

        <div className="flex gap-2 mb-3">
          <select data-testid="membership-preview-type-select" className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="upcoming">Próximo a vencer</option>
            <option value="expired">Vencido</option>
          </select>
          {type === "upcoming" && (
            <select data-testid="membership-preview-days-select" className={inputCls} value={selectedDays} onChange={(e) => setSelectedDays(Number(e.target.value))}>
              {(days.length ? days : [7]).map((d) => (
                <option key={d} value={d}>{d} día(s) antes</option>
              ))}
            </select>
          )}
        </div>
        <button data-testid="membership-preview-fetch-btn" onClick={fetchPreview} disabled={busy} className="w-full bg-gold text-black font-bold rounded-md py-2 text-sm hover:brightness-95 transition-all disabled:opacity-60 mb-4">
          {busy ? "Generando…" : "Ver previsualización"}
        </button>

        {data && (
          <div className="space-y-3">
            <div>
              <span className="overline text-muted-foreground">Mensaje al grupo</span>
              <div data-testid="membership-preview-group-message" className="text-sm bg-obsidian border border-white/10 rounded-md p-3 mt-1 whitespace-pre-wrap">{data.groupMessage}</div>
            </div>
            <div>
              <span className="overline text-muted-foreground">DM a cada contacto de renovación</span>
              <div data-testid="membership-preview-admin-dm" className="text-sm bg-obsidian border border-white/10 rounded-md p-3 mt-1 whitespace-pre-wrap">{data.adminDmMessage}</div>
            </div>
            <div data-testid="membership-preview-contacts" className="text-xs text-muted-foreground">
              {data.renewalContacts.length
                ? `Se enviará DM a: ${data.renewalContacts.map((c) => `${c.name} (+${c.identifier})`).join(", ")}`
                : "No hay contactos de renovación activos configurados: no se enviaría ningún DM."}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MembershipNotices({ groups, reload }) {
  const [days, setDays] = useState([7, 3, 1]);
  const [daysInput, setDaysInput] = useState("7, 3, 1");
  const [savingDays, setSavingDays] = useState(false);
  const [previewGroup, setPreviewGroup] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const s = await api.membershipSettings();
      setDays(s.noticeDaysBeforeExpiry || [7, 3, 1]);
      setDaysInput((s.noticeDaysBeforeExpiry || [7, 3, 1]).join(", "));
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveDays = async () => {
    const parsed = daysInput.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => Number.isFinite(n) && n > 0);
    if (!parsed.length) {
      toast.error("Indica al menos un día válido, ej: 7, 3, 1");
      return;
    }
    setSavingDays(true);
    try {
      const res = await api.updateMembershipSettings({ noticeDaysBeforeExpiry: parsed });
      setDays(res.noticeDaysBeforeExpiry);
      setDaysInput(res.noticeDaysBeforeExpiry.join(", "));
      toast.success("Umbrales de aviso guardados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingDays(false);
    }
  };

  const toggleGroupNotify = async (g) => {
    try {
      await api.updateConfig(g.chatId, { membershipNotify: !(g.config || {}).membershipNotify });
      toast.success((g.config || {}).membershipNotify ? "Avisos de membresía desactivados" : "Avisos de membresía activados");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const eligible = groups.filter((g) => g.licenseState?.everActivated);

  return (
    <div className="space-y-4">
      <div className="card-surface p-6" data-testid="membership-days-card">
        <h3 className="font-heading font-semibold mb-1">Días de aviso previo</h3>
        <p className="text-xs text-muted-foreground mb-3">Umbrales globales (en días antes del vencimiento). Por defecto: 7, 3, 1. Aplica al enviar el aviso al grupo y a cada contacto de renovación.</p>
        <div className="flex gap-2 max-w-md">
          <input data-testid="membership-days-input" className={inputCls} value={daysInput} onChange={(e) => setDaysInput(e.target.value)} placeholder="7, 3, 1" />
          <button data-testid="membership-days-save" onClick={saveDays} disabled={savingDays} className="bg-gold text-black font-bold rounded-md px-4 py-2.5 text-sm hover:brightness-95 transition-all disabled:opacity-60 flex items-center gap-1.5 shrink-0">
            <Save size={15} /> Guardar
          </button>
        </div>
      </div>

      <div className="card-surface overflow-hidden" data-testid="membership-groups-list">
        <div className="px-5 py-3 border-b border-white/10">
          <span className="overline">Avisos por grupo ({eligible.length})</span>
        </div>
        {eligible.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Aún no hay grupos con plan activado.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {eligible.map((g) => {
              const on = !!(g.config || {}).membershipNotify;
              return (
                <div key={g.chatId} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3.5 hover:bg-white/[0.02]" data-testid={`membership-group-row-${g.chatId}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{g.name || "Grupo sin nombre"}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">{g.chatId}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`membership-notice-toggle-${g.chatId}`}
                      onClick={() => toggleGroupNotify(g)}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-md border transition-all ${on ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-white/5 text-muted-foreground border-white/10"}`}
                    >
                      {on ? <BellRing size={13} /> : <BellOff size={13} />} {on ? "Activados" : "Desactivados"}
                    </button>
                    <button data-testid={`membership-preview-btn-${g.chatId}`} onClick={() => setPreviewGroup(g)} className="flex items-center gap-1.5 text-xs border border-white/15 rounded-md px-2.5 py-1.5 hover:bg-white/5 transition-all">
                      <Eye size={13} /> Previsualizar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {previewGroup && <PreviewModal group={previewGroup} days={days} onClose={() => setPreviewGroup(null)} />}
    </div>
  );
}
